/**
 * ContentForge — Office View (Pixel-Art Virtual Office)
 *
 * Full office background image with character avatars absolutely positioned.
 * Working = at desk. Idle = wandering the office. Fits one viewport, no scroll.
 */

import { html, nothing } from "lit";
import type {
  ContentForgeAgentState,
  ContentForgeActivityEntry,
} from "../controllers/contentforge.ts";

// ─── Types ──────────────────────────────────────────────────────────────

export type CfOfficeProps = {
  agents: ContentForgeAgentState[];
  recentActivity: ContentForgeActivityEntry[];
  arthurPaused: boolean;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
};

// ─── Agent positions (% on office background image) ─────────────────────

type AgentConfig = {
  id: string;
  name: string;
  role: string;
  avatar: string;
  /** Position when working (at their desk) — percentage x,y */
  deskX: number;
  deskY: number;
  /** Position when idle (wandering) — percentage x,y */
  idleX: number;
  idleY: number;
};

// Desk layout matching the background image:
//   Row 1 (top):    4 desks   at ~25% Y
//   Row 2 (middle): 4 desks   at ~48% Y
//   Arthur (front): 1 desk    at ~78% Y, center
//
// Idle positions: near coffee machine, water cooler, bookshelves, open areas
const AGENTS: AgentConfig[] = [
  {
    id: "arthur",
    name: "Arthur",
    role: "Manager",
    avatar: "/agents/ARTHUR.webp",
    deskX: 48,
    deskY: 78,
    idleX: 60,
    idleY: 68,
  },
  {
    id: "strategist",
    name: "Strategist",
    role: "Planner",
    avatar: "/agents/STRATEGIST.webp",
    deskX: 28,
    deskY: 25,
    idleX: 22,
    idleY: 55,
  },
  {
    id: "miner",
    name: "Miner",
    role: "Keywords",
    avatar: "/agents/MINER.webp",
    deskX: 42,
    deskY: 25,
    idleX: 35,
    idleY: 15,
  },
  {
    id: "sourcer",
    name: "Sourcer",
    role: "Research",
    avatar: "/agents/SOURCER.webp",
    deskX: 58,
    deskY: 25,
    idleX: 50,
    idleY: 15,
  },
  {
    id: "writer",
    name: "Writer",
    role: "Content",
    avatar: "/agents/WRITER.webp",
    deskX: 72,
    deskY: 25,
    idleX: 75,
    idleY: 15,
  },
  {
    id: "artist",
    name: "Artist",
    role: "Images",
    avatar: "/agents/ARTIST.webp",
    deskX: 28,
    deskY: 47,
    idleX: 22,
    idleY: 68,
  },
  {
    id: "linker",
    name: "Linker",
    role: "SEO Links",
    avatar: "/agents/LINKER.webp",
    deskX: 42,
    deskY: 47,
    idleX: 35,
    idleY: 60,
  },
  {
    id: "publisher",
    name: "Publisher",
    role: "Publishing",
    avatar: "/agents/PUBLISHER.webp",
    deskX: 58,
    deskY: 47,
    idleX: 65,
    idleY: 60,
  },
  {
    id: "auditor",
    name: "Auditor",
    role: "Quality",
    avatar: "/agents/AUDITOR.webp",
    deskX: 72,
    deskY: 47,
    idleX: 75,
    idleY: 55,
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────

function getState(agents: ContentForgeAgentState[], id: string): ContentForgeAgentState | null {
  return agents.find((a) => a.type === id) ?? null;
}

function timeAgo(ts: number | null): string {
  if (!ts) {
    return "";
  }
  const m = Math.floor((Date.now() - ts) / 60_000);
  if (m < 1) {
    return "now";
  }
  if (m < 60) {
    return `${m}m ago`;
  }
  const h = Math.floor(m / 60);
  if (h < 24) {
    return `${h}h ago`;
  }
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Main Render ────────────────────────────────────────────────────────

export function renderCfOffice(props: CfOfficeProps) {
  const { agents, recentActivity, arthurPaused, error, onRefresh } = props;

  const errors = agents.filter((a) => a.status === "error").length;

  return html`
    <div class="ofc">
      <!-- Top Bar -->
      <div class="ofc-bar">
        <div class="ofc-bar__left">
          <h2 class="ofc-bar__title">The Office</h2>
          <span class="ofc-bar__badge ofc-bar__badge--${arthurPaused ? "off" : "on"}">
            ${arthurPaused ? "⏸ Paused" : "▶ Active"}
          </span>
        </div>
        <div class="ofc-bar__right">
          <span class="ofc-bar__legend"><i class="ofc-dot ofc-dot--green"></i>Working</span>
          <span class="ofc-bar__legend"><i class="ofc-dot ofc-dot--yellow"></i>Idle</span>
          ${
            errors > 0
              ? html`
                  <span class="ofc-bar__legend"><i class="ofc-dot ofc-dot--red"></i>Error</span>
                `
              : nothing
          }
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${onRefresh}>↻ Refresh</button>
        </div>
      </div>

      ${error ? html`<div class="cf-error" style="padding:0.5rem 1rem">${error}</div>` : nothing}

      <!-- Main body: office + sidebar -->
      <div class="ofc-body">
        <!-- Office Scene -->
        <div class="ofc-scene">
          <div class="ofc-scene__inner">
            ${AGENTS.map((cfg) => {
              const state = getState(agents, cfg.id);
              const st = state?.status ?? "idle";
              const atDesk = st === "working" || st === "error";
              const x = atDesk ? cfg.deskX : cfg.idleX;
              const y = atDesk ? cfg.deskY : cfg.idleY;
              const task = state?.currentTask;

              return html`
                <div
                  class="ofc-agent ofc-agent--${st}"
                  style="left:${x}%;top:${y}%"
                  title="${cfg.name} — ${st === "working" ? (task ?? "Working") : st === "idle" ? "On break" : st === "error" ? "Error" : "Offline"}"
                >
                  ${atDesk && task ? html`<div class="ofc-bubble">${task.slice(0, 30)}…</div>` : nothing}
                  <img class="ofc-agent__img" src="${cfg.avatar}" alt="${cfg.name}" />
                  <div class="ofc-agent__label">
                    <span class="ofc-agent__dot" style="background:${st === "working" ? "#22c55e" : st === "error" ? "#ef4444" : st === "idle" ? "#eab308" : "#475569"}"></span>
                    <span class="ofc-agent__name">${cfg.name}</span>
                  </div>
                </div>
              `;
            })}
          </div>
        </div>

        <!-- Right Sidebar: Live Activity -->
        <div class="ofc-sidebar">
          <div class="ofc-sidebar__header">
            <span>⚡ Live Activity</span>
            <span class="ofc-sidebar__time">${recentActivity.length > 0 ? timeAgo(recentActivity[0]?.timestamp) : ""}</span>
          </div>
          <div class="ofc-sidebar__list">
            ${
              recentActivity.length > 0
                ? recentActivity.slice(0, 10).map(
                    (e) => html`
              <div class="ofc-sidebar__item ofc-sidebar__item--${e.status}">
                <span class="ofc-sidebar__item-agent">${e.agentType}</span>
                <span class="ofc-sidebar__item-text">${e.details.slice(0, 50)}</span>
                <span class="ofc-sidebar__item-time">${timeAgo(e.timestamp)}</span>
              </div>
            `,
                  )
                : html`
                    <div class="ofc-sidebar__empty">
                      <span>✨</span>
                      <span>No recent activity</span>
                      <span>Events will appear here</span>
                    </div>
                  `
            }
          </div>
        </div>
      </div>
    </div>
  `;
}
